import React from 'react';
import {
  Autocomplete, Box, Button, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ConditionEditorProps } from './props';
import {
  Condition, Operateur, Question, LIMITES, LIBELLES_OPERATEURS, OPERATEURS, OPERATEURS_LISTE, OPERATEURS_PAR_TYPE,
  OPERATEURS_SANS_VALEUR, REGEX, nettoyerCondition, optionsCible,
} from './types';
import { decrireConditionCible, libelleQuestion } from './libelles';

// Bloc « Afficher seulement si… » d'une question ou d'une section : cible (parmi les candidates antérieures, déjà
// filtrées par le parent via questionsAnterieures) → opérateur (OPERATEURS_PAR_TYPE du type de la cible) → valeur
// (Select des options, saisie libre, ou rien avec rempli / vide). Composant pur : toute modification passe par
// onChange, la condition renvoyée est déjà passée par nettoyerCondition (forme de `valeur` conforme à schema.js).

// Premier opérateur compatible avec la cible (egal pour la plupart des types, contient pour choix_multiple, rempli
// pour fichier)
const premierOperateur = (cible: Question): Operateur => {
  const ops = OPERATEURS_PAR_TYPE[cible.type] || [];
  return ops.length ? ops[0] : 'rempli';
};

// Valeur(s) de la condition sous forme de tableau (toujours des chaînes)
const valeursDe = (c: Condition): string[] => {
  if (Array.isArray(c.valeur)) return c.valeur;
  return c.valeur !== undefined && c.valeur !== '' ? [c.valeur] : [];
};

const ConditionEditor: React.FC<ConditionEditorProps> = ({ condition, candidates, onChange, erreur, libellePorteur }) => {
  // Préfixe des id InputLabel / labelId, unique par instance (deux conditions peuvent viser la même question)
  const idBase = React.useId();

  // Ajout : première candidate, premier opérateur compatible, pas de valeur (à choisir ensuite)
  const ajouter = () => {
    if (!candidates.length) return;
    const cible = candidates[0];
    onChange(nettoyerCondition({ question: cible.id, op: premierOperateur(cible) }));
  };

  if (!condition) {
    const bouton = (
      <Button size="small" startIcon={<AddIcon />} disabled={!candidates.length} onClick={ajouter}>
        Ajouter une condition d'affichage
      </Button>
    );
    return (
      <Box>
        {candidates.length ? bouton : (
          <Tooltip title="Aucune question antérieure utilisable">
            <span>{bouton}</span>
          </Tooltip>
        )}
        {erreur && <FormHelperText error>{erreur}</FormHelperText>}
      </Box>
    );
  }

  const cible = candidates.filter((q) => q.id === condition.question)[0];
  const cibleIntrouvable = !cible; // cible supprimée, déplacée après le porteur, ou devenue info
  const op = condition.op;
  const opsCompatibles: Operateur[] = cible ? OPERATEURS_PAR_TYPE[cible.type] : OPERATEURS;
  const opIncompatible = opsCompatibles.indexOf(op) < 0; // type de la cible changé depuis la saisie
  const sansValeur = OPERATEURS_SANS_VALEUR.indexOf(op) >= 0;
  const liste = OPERATEURS_LISTE.indexOf(op) >= 0;
  const options = cible ? optionsCible(cible) : null;
  const valeurs = valeursDe(condition);
  const valeurScalaire = valeurs.length ? valeurs[0] : '';

  // Changement de cible : opérateur remis au premier compatible, valeur réinitialisée
  const changerCible = (e: SelectChangeEvent<string>) => {
    const nouvelle = candidates.filter((q) => q.id === e.target.value)[0];
    if (!nouvelle) return;
    onChange(nettoyerCondition({ question: nouvelle.id, op: premierOperateur(nouvelle) }));
  };

  // Changement d'opérateur : nettoyerCondition met `valeur` en forme (retirée, tableau ou scalaire)
  const changerOperateur = (e: SelectChangeEvent<string>) => {
    onChange(nettoyerCondition({ question: condition.question, op: e.target.value as Operateur, valeur: condition.valeur }));
  };

  const changerValeur = (v: string | string[] | undefined) => {
    onChange(nettoyerCondition({ question: condition.question, op, valeur: v === '' ? undefined : v }));
  };

  // Contrôle local de la valeur (même règle que schema.js : le 400 ne devrait jamais arriver jusqu'ici)
  const aideValeur = (): string | undefined => {
    if (sansValeur || opIncompatible) return undefined;
    if (liste) return valeurs.length ? undefined : 'Choisissez au moins une valeur';
    if (!valeurScalaire) return 'Valeur requise';
    if (cible && cible.type === 'nombre' && !REGEX.numerique.test(valeurScalaire)) return 'Nombre attendu (ex. 1950 ou 12.5)';
    if (cible && cible.type === 'date' && !REGEX.dateIso.test(valeurScalaire)) return 'Date attendue';
    return undefined;
  };
  const helperValeur = aideValeur();

  // Champ valeur selon le type de la cible et l'opérateur
  const champValeur = (): React.ReactNode => {
    if (sansValeur || opIncompatible || cibleIntrouvable || !cible) return null;

    if (options) {
      // choix / choix_multiple / oui_non : options de la cible (valeurs orphelines conservées en grisé)
      const orphelines = valeurs.filter((v) => !options.some((o) => o.valeur === v));
      const libelleOption = (v: string): string => {
        const o = options.filter((x) => x.valeur === v)[0];
        return o && o.libelle ? o.libelle : v;
      };
      const items = [
        ...options.map((o) => <MenuItem key={o.valeur} value={o.valeur}>{o.libelle || o.valeur}</MenuItem>),
        ...orphelines.map((v) => <MenuItem key={`orpheline_${v}`} value={v} disabled>{v} (option inconnue)</MenuItem>),
      ];
      const aide = !options.length ? "La question cible n'a pas encore d'option" : helperValeur;
      if (liste) {
        return (
          <FormControl size="small" fullWidth error={!!aide}>
            <InputLabel id={`${idBase}-valeurs`}>Valeurs</InputLabel>
            <Select<string[]>
              labelId={`${idBase}-valeurs`}
              multiple
              label="Valeurs"
              value={valeurs}
              onChange={(e) => changerValeur(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
              renderValue={(sel) => sel.map(libelleOption).join(', ')}
            >
              {items}
            </Select>
            {aide && <FormHelperText>{aide}</FormHelperText>}
          </FormControl>
        );
      }
      return (
        <FormControl size="small" fullWidth error={!!aide}>
          <InputLabel id={`${idBase}-valeur`}>Valeur</InputLabel>
          <Select<string>
            labelId={`${idBase}-valeur`}
            label="Valeur"
            value={valeurScalaire}
            onChange={(e) => changerValeur(e.target.value)}
          >
            {items}
          </Select>
          {aide && <FormHelperText>{aide}</FormHelperText>}
        </FormControl>
      );
    }

    if (cible.type === 'nombre') {
      if (liste) {
        // parmi / pas_parmi : plusieurs nombres saisis un à un (Entrée), rendus en chips
        const invalides = valeurs.filter((v) => !REGEX.numerique.test(v));
        const aide = invalides.length ? `Valeur non numérique : ${invalides.join(', ')}` : helperValeur;
        return (
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            size="small"
            fullWidth
            options={[]}
            value={valeurs}
            onChange={(_, vals) => changerValeur(vals.map((v) => String(v).trim()).filter((v) => !!v))}
            renderTags={(vals, getTagProps) => vals.map((v, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${v}_${index}`}
                size="small"
                label={v}
                color={REGEX.numerique.test(v) ? 'default' : 'error'}
              />
            ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Valeurs"
                placeholder="Nombre puis Entrée"
                error={!!aide}
                helperText={aide || 'Entrée après chaque nombre'}
              />
            )}
          />
        );
      }
      return (
        <TextField
          size="small"
          fullWidth
          type="number"
          label="Valeur"
          value={valeurScalaire}
          onChange={(e) => changerValeur(e.target.value)}
          inputProps={{ step: 'any' }}
          error={!!helperValeur}
          helperText={helperValeur}
        />
      );
    }

    if (cible.type === 'date') {
      return (
        <TextField
          size="small"
          fullWidth
          type="date"
          label="Valeur"
          value={valeurScalaire}
          onChange={(e) => changerValeur(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!helperValeur}
          helperText={helperValeur}
        />
      );
    }

    // texte / texte_long (fichier n'arrive pas ici : rempli / vide seulement)
    return (
      <TextField
        size="small"
        fullWidth
        label="Valeur"
        value={valeurScalaire}
        onChange={(e) => changerValeur(e.target.value)}
        inputProps={{ maxLength: LIMITES.valeurTexte }}
        error={!!helperValeur}
        helperText={helperValeur}
      />
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Afficher seulement si…</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        {/* Cible */}
        <FormControl size="small" fullWidth error={cibleIntrouvable}>
          <InputLabel id={`${idBase}-cible`}>Question</InputLabel>
          <Select<string>
            labelId={`${idBase}-cible`}
            label="Question"
            value={condition.question}
            onChange={changerCible}
            renderValue={(id) => libelleQuestion(candidates.filter((q) => q.id === id)[0], id)}
          >
            {candidates.map((q) => (
              <MenuItem key={q.id} value={q.id}>
                <ListItemText primary={libelleQuestion(q)} secondary={q.id} />
              </MenuItem>
            ))}
            {cibleIntrouvable && (
              <MenuItem value={condition.question} disabled>
                <ListItemText primary={condition.question} secondary="introuvable ou non antérieure" />
              </MenuItem>
            )}
          </Select>
          {cibleIntrouvable && (
            <FormHelperText>Cette question n'existe plus ou n'est plus placée avant : choisissez une autre cible</FormHelperText>
          )}
        </FormControl>

        {/* Opérateur */}
        <FormControl size="small" fullWidth error={opIncompatible}>
          <InputLabel id={`${idBase}-op`}>Opérateur</InputLabel>
          <Select<string>
            labelId={`${idBase}-op`}
            label="Opérateur"
            value={op}
            onChange={changerOperateur}
          >
            {opsCompatibles.map((o) => <MenuItem key={o} value={o}>{LIBELLES_OPERATEURS[o]}</MenuItem>)}
            {opIncompatible && <MenuItem value={op} disabled>{LIBELLES_OPERATEURS[op] || op} (incompatible)</MenuItem>}
          </Select>
          {opIncompatible && <FormHelperText>Opérateur incompatible avec le type de la cible : choisissez-en un autre</FormHelperText>}
        </FormControl>

        {/* Valeur */}
        {champValeur()}

        <Button
          size="small"
          color="inherit"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => onChange(undefined)}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Retirer la condition
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        <strong>{libellePorteur || 'Cet élément'}</strong> s'affiche seulement si {decrireConditionCible(cible, condition)}
      </Typography>
      {erreur && <FormHelperText error>{erreur}</FormHelperText>}
    </Box>
  );
};

export default ConditionEditor;
